import React, { FC, useRef, useState, useEffect } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  ScrollView,
  Pressable,
  Button,
} from "native-base";
import * as AV from "../../AVCore/AVCore";
import Tooltip from "@mui/material/Tooltip";
import { ExpandIcon, CollapseIcon } from "../../assets/icons/icon-assets";
import AVCancel from "./AVCancel";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import { Loc } from "../../App/Loc";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  setPostPopupExpanded,
  setLiveStreamToken,
} from "../../Redux/Actions/userActions";
import AgoraRTC from "agora-rtc-sdk-ng";

/* -------------------- AGORA CONFIG -------------------- */
const AGORA_APP_ID = "914bc52b86f54cec8d68aa41a00e2ec6";
const CHANNEL_NAME = "test_live_channel";
const TEMP_TOKEN =
  "007eJxTYOjWdHkZ8lKbc+K+D/Luh30MjZpkJQyO7zCZUbt64U91tyAFBktDk6RkU6MkC7M0U5Pk1GSLFDOLxEQTw0QDg1Sj1GSzXyzFmQ2BjAy/Q5SZGRkgEMQXZChJLS6Jz8ksS41PzkjMy0vNYWAAAJS8I4c=";

interface AVLiveViewPopupProps {
  onClose: () => void;
}

const AVLiveViewPopup: FC<AVLiveViewPopupProps> = ({ onClose }) => {
  const dimensions = useWindowDimension();
  const mobile = dimensions?.width < 600;
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  const clientRef = useRef<any>(null);
  const remoteVideoRef = useRef<HTMLDivElement | null>(null);

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const liveStreamToken = useSelector(
    (state: RootStateOrAny) => state.user.liveStreamToken,
  );

  /* -------------------- JOIN LIVE (VIEWER) -------------------- */
  useEffect(() => {
    joinLive();

    return () => {
      void leaveLive();
    };
  }, []);

  const joinLive = async () => {
    const client = AgoraRTC.createClient({
      mode: "live",
      codec: "vp8",
    });

    clientRef.current = client;
    client.setClientRole("audience");

    client.on("user-published", async (user, mediaType) => {
      await client.subscribe(user, mediaType);

      if (mediaType === "video" && user.videoTrack) {
        user.videoTrack.play(remoteVideoRef.current!);
        setHasRemoteVideo(true);
      }

      if (mediaType === "audio" && user.audioTrack) {
        user.audioTrack.play();
      }
    });

    try {
      let token = liveStreamToken;

      // If no token, fetch first
      if (!token) {
        token = await AV.AVEntity.getSecret(currentUser?.region);
        dispatch(setLiveStreamToken(token));
      }

      await client.join(AGORA_APP_ID, CHANNEL_NAME, token, null);

      console.log("Viewer joined live");
    } catch (error) {
      console.log("Token expired or invalid, fetching new one...", error);

      const newToken = await AV.AVEntity.getSecret(currentUser?.region);

      if (newToken) {
          dispatch(setLiveStreamToken(newToken));

        await client.join(AGORA_APP_ID, CHANNEL_NAME, newToken, null);

        console.log("Viewer joined live with new token");
      }
    }
  };

  const leaveLive = async () => {
    await clientRef.current?.leave();
    clientRef.current = null;
  };

  /* -------------------- UI -------------------- */
  return (
    <Box
      position="fixed"
      bg="rgba(0,0,0,0.25)"
      top={0}
      bottom={0}
      left={0}
      right={0}
      zIndex={99999}
    >
      <div
        className={`create_post_popup ${mobile && "create_post_popup_mobile"}`}
        style={{
          width: isExpanded
            ? mobile
              ? "100%"
              : "90%"
            : mobile
              ? dimensions.width - 15
              : "700px",
          height: isExpanded ? (mobile ? "100%" : "90vh") : "60vh",
          padding: isExpanded ? "30px" : "24px",
          top: isExpanded ? "50%" : "55%",
        }}
      >
        <VStack height="100%">
          {/* HEADER */}
          <HStack
            justifyContent="space-between"
            alignItems="center"
            borderBottomWidth={1}
            borderColor="AVColor.grey"
            pb={3}
          >
            <Text fontSize={24}>Live Viewer</Text>

            <HStack space={3} alignItems="center">
              <Tooltip
                title={
                  isExpanded
                    ? Loc.currentLang.postPopup.collapse
                    : Loc.currentLang.postPopup.expand
                }
                arrow
              >
                <Pressable
                  onPress={() => {
                    setIsExpanded((prev) => {
                      dispatch(setPostPopupExpanded(!prev));
                      return !prev;
                    });
                  }}
                >
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </Pressable>
              </Tooltip>

              <AVCancel
                callback={() => {
                  void leaveLive();
                  onClose();
                  dispatch(setPostPopupExpanded(false));
                }}
                color={undefined}
                size={undefined}
              />
            </HStack>
          </HStack>

          {/* BODY */}
          <ScrollView
            showsVerticalScrollIndicator={false}
            maxHeight={isExpanded ? "78vh" : "45vh"}
          >
            <Box
              mt={6}
              height={isExpanded ? "61vh" : "40vh"}
              borderWidth={1}
              borderRadius={5}
              borderColor="AVColor.grey"
              bg="black"
              position="relative"
            >
              {!hasRemoteVideo && (
                <Text
                  position="absolute"
                  color="white"
                  top="50%"
                  left="50%"
                  style={{
                    transform: [{ translateX: -50 }, { translateY: -50 }],
                  }}
                >
                  Waiting for host…
                </Text>
              )}

              {/* REMOTE VIDEO */}
              <Box
                ref={remoteVideoRef}
                width="100%"
                height="100%"
                borderRadius={5}
                overflow="hidden"
              />
            </Box>
          </ScrollView>

          {/* FOOTER */}
          <HStack justifyContent="flex-end" space={3} mt={4}>
            <Button
              height="40px"
              width="180px"
              bg="rgba(238,238,238,1)"
              borderRadius="full"
              onPress={() => {
                void leaveLive();
                onClose();
                dispatch(setPostPopupExpanded(false));
              }}
            >
              <Text color="rgba(0,0,0,0.5)">
                {Loc.currentLang.postPopup.cancel}
              </Text>
            </Button>
          </HStack>
        </VStack>
      </div>
    </Box>
  );
};

export default AVLiveViewPopup;
